import { ReactNode, useCallback } from 'react'

import { useGetSignal } from '@/api/signals.hook'
import { Alert } from '@/gen/inventory/v1/alert_pb'

import { WHY_THIS_MATTERS_LABEL } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'

import {
  DEFAULT_EXPORT_DOCUMENT_TITLE,
  DESCRIPTION_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
} from '@/pages/alert-details/labels'
import { SignalDetailsHeader } from '@/pages/alert-details/signal-header'

import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { AlertSeverityBadge } from '@/components/shared/alert-severity-badge/alert-severity-badge'
import { AlertStatusBadge } from '@/components/shared/alert-status-badge/alert-status-badge'
import { AnalysisQuotes } from '@/components/shared/analysis-quotes/analysis-quotes'
import { signalDetailQuotes } from '@/components/shared/analysis-quotes/utils'
import { ExportButton } from '@/components/shared/export-button/export-button'
import { Markdown } from '@/components/shared/markdown/markdown'

type ExportSignalProps = {
  signalId: string
  alert?: Alert
}

export const ExportSignal = ({ signalId, alert }: ExportSignalProps) => {
  const { data, isLoading, isError } = useGetSignal(signalId, true)

  const getExportContent = useCallback(() => {
    if (isLoading || !data || isError || !data?.signal) {
      return {
        exportContent: null,
        renderContent: () => {},
        isLoaded: false,
      }
    }

    const signal = data.signal

    const exportContent: ReactNode = (
      <div className='m-8'>
        <SignalDetailsHeader
          titleVariant={'showAll'}
          readOnly
          title={signal.title ?? ''}
          subtitle={signal.subtitle ?? ''}
          id={signal.id}
          visible={signal.visible}
          company={signal.companySummary}
          signalType={signal.signalType}
        />
        {alert && (
          <div className='mb-6 flex items-center gap-6'>
            <div className='flex items-center gap-2'>
              <span className='text-main-foreground-secondary text-label-small'>
                {SEVERITY_LABEL}:
              </span>
              <AlertSeverityBadge level={alert.manualSeverity || alert.severity} />
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-main-foreground-secondary text-label-small'>
                {STATUS_LABEL}:
              </span>
              <AlertStatusBadge status={alert.workStatus} />
            </div>
          </div>
        )}
        <div>
          <h1 className='font-semibold leading-10'>{DESCRIPTION_LABEL}</h1>
          <Markdown>{data.detail?.documentation?.description}</Markdown>
        </div>
        <br />
        <div>
          <h1 className='font-semibold leading-10'>{WHY_THIS_MATTERS_LABEL}</h1>
          <Markdown>{data.detail?.documentation?.risk}</Markdown>
        </div>
        {signalDetailQuotes(data.detail).length > 0 && (
          <>
            <Separator className='my-12' />
            <AnalysisQuotes quotes={signalDetailQuotes(data.detail)} readonly />
          </>
        )}
      </div>
    )

    return {
      exportContent,
      renderContent: () => {},
      isLoaded: true,
    }
  }, [data, isLoading, isError, alert])

  const trackExport = useTrackCallback('alert.export')

  return (
    <ExportButton
      data-dd-action-name='alert.export'
      onClick={() =>
        trackExport({
          signalTitle: data?.signal?.title,
        })
      }
      documentTitle={data?.signal?.title ?? DEFAULT_EXPORT_DOCUMENT_TITLE}
      getExportContent={getExportContent}
      variant='ghost'
    />
  )
}
